/* eslint-disable lingui/no-unlocalized-strings -- Intl.DateTimeFormat option values, not user-facing copy */
import { i18n } from "~/i18n/i18n";

export function isOlderThanMonths(
  dateInput: string | Date | null | undefined,
  months: number,
): boolean {
  if (!dateInput) return false;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return false;

  const comparison = new Date();
  comparison.setMonth(comparison.getMonth() - months);
  return date < comparison;
}

export type DateInput = Date | string | number | null | undefined;

export interface DateFormatOptions {
  /** Override the active locale (defaults to the active Lingui locale). */
  locale?: string;
  /** IANA timezone (defaults to the runtime's local timezone). */
  timeZone?: string;
}

function toDate(input: DateInput): Date | null {
  if (input == null) return null;
  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Account language codes are stored bare (en, fr, ...). Intl treats bare "en"
// as en-US (month-first, 12h), but the app has always shown English dates
// day-first/24h, so pin English to en-GB to preserve that. Other locales pass
// through unchanged.
const LOCALE_OVERRIDES: Record<string, string> = { en: "en-GB" };

function activeLocale(locale?: string): string {
  const active = locale || i18n.locale || "en";
  return LOCALE_OVERRIDES[active] ?? active;
}

function format(
  input: DateInput,
  options: Intl.DateTimeFormatOptions,
  o?: DateFormatOptions,
  resolvedLocale?: string,
): string {
  const date = toDate(input);
  if (!date) return "";
  const opts: Intl.DateTimeFormatOptions = { ...options };
  if (o?.timeZone) opts.timeZone = o.timeZone;
  const locale = resolvedLocale ?? activeLocale(o?.locale);
  return new Intl.DateTimeFormat(locale, opts).format(date);
}

// 24-hour, zero-padded clock — matches the legacy moment "HH:mm".
const TIME_24H: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

// English call sites historically used a 12-hour clock; keep that for English
// and let every other locale use its own convention (the ones we support all
// render 24h). Decided per resolved locale so the clock follows the viewer.
function clockParts(locale: string): Intl.DateTimeFormatOptions {
  return locale.startsWith("en")
    ? { hour: "numeric", minute: "2-digit", hour12: true }
    : TIME_24H;
}

/** "30 Oct 2026" — the default app-wide date format. */
export function formatDate(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "numeric", month: "short", year: "numeric" }, o);
}

/** "30 October 2026" */
export function formatDateLong(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "numeric", month: "long", year: "numeric" }, o);
}

/** "30/10/2026" (locale-ordered numeric date). */
export function formatNumericDate(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "2-digit", month: "2-digit", year: "numeric" }, o);
}

/** "30 Oct" */
export function formatMonthDay(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "numeric", month: "short" }, o);
}

/** "30 October" */
export function formatMonthDayLong(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "numeric", month: "long" }, o);
}

/** "Thu 30" */
export function formatWeekdayDay(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { weekday: "short", day: "numeric" }, o);
}

/** "Friday 30 October" — weekday + day + long month, no year. */
export function formatWeekdayDayMonthLong(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { weekday: "long", day: "numeric", month: "long" }, o);
}

/** "Fri, 30 Oct 2026" — weekday + day-first date (e.g. live-event headers). */
export function formatWeekdayDate(input: DateInput, o?: DateFormatOptions): string {
  return format(
    input,
    { weekday: "short", day: "numeric", month: "short", year: "numeric" },
    o,
  );
}

/** "30" (zero-padded day of month, e.g. calendar tiles). */
export function formatDayOfMonth(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { day: "2-digit" }, o);
}

/** "Oct" (abbreviated month, e.g. calendar tiles). */
export function formatMonthAbbrev(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { month: "short" }, o);
}

/** "18:00" / "09:05" — 24h clock (matches the legacy "HH:mm"). */
export function formatTime(input: DateInput, o?: DateFormatOptions): string {
  return format(input, TIME_24H, o);
}

/** "6:00 pm" for English, locale 24h otherwise — the agenda's 12h clock. */
export function formatTimeMeridiem(input: DateInput, o?: DateFormatOptions): string {
  const locale = activeLocale(o?.locale);
  return format(input, clockParts(locale), o, locale);
}

/** "18:00 UTC" — 24h time with timezone abbreviation. */
export function formatTimeTz(input: DateInput, o?: DateFormatOptions): string {
  return format(input, { ...TIME_24H, timeZoneName: "short" }, o);
}

/** "30 Oct 2026, 6:00 pm" (English 12h; other locales 24h). */
export function formatDateTime(input: DateInput, o?: DateFormatOptions): string {
  const locale = activeLocale(o?.locale);
  return format(
    input,
    { day: "numeric", month: "short", year: "numeric", ...clockParts(locale) },
    o,
    locale,
  );
}

/** "30 Oct, 18:00 UTC" — short date + 24h time + timezone. */
export function formatShortDateTimeTz(input: DateInput, o?: DateFormatOptions): string {
  return format(
    input,
    { month: "short", day: "numeric", ...TIME_24H, timeZoneName: "short" },
    o,
  );
}

/** "Friday 30 Oct, 6:00 pm UTC" — full live-session label (English 12h). */
export function formatEventDateTime(input: DateInput, o?: DateFormatOptions): string {
  const locale = activeLocale(o?.locale);
  return format(
    input,
    {
      weekday: "long",
      month: "short",
      day: "numeric",
      ...clockParts(locale),
      timeZoneName: "short",
    },
    o,
    locale,
  );
}

export default formatDate;
